import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import TBDatePicker from '../../../../shared/components/TBDatePicker/TBDatePicker';
import { focusWithinRing } from '../../../../shared/theme/focusStyles';

/* The first format is what the field shows; the numeric ones still parse a
   date typed the old way. */
const DATE_FORMATS = ['EEE MMM d, yyyy', 'MM/dd/yyyy', 'M/d/yyyy'];

const DATE_PICKER_STYLES = {
  margin: '0',
  maxWidth: '100%',
};

const PanelContainer = styled('div')`
  width: 100%;
`;

const RangeRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
`;

const RangeSeparator = styled('span')`
  flex-shrink: 0;
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* Names the input for screen readers; the mocks call for no visible label. */
const VisuallyHidden = styled('span')`
  position: absolute;
  overflow: hidden;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

/* Wraps only the date field so the focus border hugs the input and its
   calendar icon. */
const PickerFocusWrap = styled('div')`
  position: relative;
  flex: 1;
  min-width: 0;
  ${focusWithinRing}
`;

const CopyDateInputs = ({
  panelId,
  labelledBy,
  mode,
  startDateString,
  endDateString,
  startDate,
  onStartChange,
  onEndChange,
  onPickerKeyDown,
  onPickerKeyDownCapture,
  isTabPanel,
  sectionTitleId,
}) => {
  const startLabelId = `${panelId}-start-label`;
  const endLabelId = `${panelId}-end-label`;

  return (
    <PanelContainer aria-labelledby={labelledBy} id={panelId} role={isTabPanel ? 'tabpanel' : undefined}>
      {mode === 'single' ? (
        <PickerFocusWrap onKeyDown={onPickerKeyDown} onKeyDownCapture={onPickerKeyDownCapture}>
          <VisuallyHidden id={startLabelId}>Date</VisuallyHidden>
          <TBDatePicker
            activeDate={startDateString}
            additionalStyles={DATE_PICKER_STYLES}
            ariaLabelledBy={`${sectionTitleId} ${startLabelId}`}
            dateFormat={DATE_FORMATS}
            inputId={`${panelId}-start`}
            maxDate={null}
            mobileFriendly
            setActiveDate={onStartChange}
            showDropShadow={false}
            showIcon
          />
        </PickerFocusWrap>
      ) : (
        <RangeRow>
          <PickerFocusWrap onKeyDown={onPickerKeyDown} onKeyDownCapture={onPickerKeyDownCapture}>
            <VisuallyHidden id={startLabelId}>Start date</VisuallyHidden>
            <TBDatePicker
              activeDate={startDateString}
              additionalStyles={DATE_PICKER_STYLES}
              dateFormat={DATE_FORMATS}
              ariaLabelledBy={`${sectionTitleId} ${startLabelId}`}
              inputId={`${panelId}-start`}
              maxDate={null}
              mobileFriendly
              setActiveDate={onStartChange}
              showDropShadow={false}
              showIcon
            />
          </PickerFocusWrap>
          <RangeSeparator aria-hidden='true'>to</RangeSeparator>
          <PickerFocusWrap onKeyDown={onPickerKeyDown} onKeyDownCapture={onPickerKeyDownCapture}>
            <VisuallyHidden id={endLabelId}>End date</VisuallyHidden>
            <TBDatePicker
              activeDate={endDateString}
              additionalStyles={DATE_PICKER_STYLES}
              dateFormat={DATE_FORMATS}
              ariaLabelledBy={`${sectionTitleId} ${endLabelId}`}
              inputId={`${panelId}-end`}
              maxDate={null}
              minDate={startDate || undefined}
              mobileFriendly
              setActiveDate={onEndChange}
              showDropShadow={false}
              showIcon
            />
          </PickerFocusWrap>
        </RangeRow>
      )}
    </PanelContainer>
  );
};

CopyDateInputs.propTypes = {
  panelId: PropTypes.string.isRequired,
  labelledBy: PropTypes.string,
  mode: PropTypes.oneOf(['single', 'range']).isRequired,
  startDateString: PropTypes.string.isRequired,
  endDateString: PropTypes.string,
  startDate: PropTypes.instanceOf(Date),
  onStartChange: PropTypes.func.isRequired,
  onEndChange: PropTypes.func,
  onPickerKeyDown: PropTypes.func.isRequired,
  onPickerKeyDownCapture: PropTypes.func.isRequired,
  isTabPanel: PropTypes.bool,
  sectionTitleId: PropTypes.string.isRequired,
};

CopyDateInputs.defaultProps = {
  isTabPanel: true,
  labelledBy: undefined,
  endDateString: '',
  startDate: null,
  onEndChange: () => {},
};

export default CopyDateInputs;
